import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Cookie, Shield, Settings, ChevronDown, ChevronUp, Check } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'benapc-cookie-consent';
const COOKIE_PREFS_KEY = 'benapc-cookie-prefs';
const COOKIE_LOG_KEY = 'benapc-consent-log';

interface CookiePrefs {
  essential: boolean; // Always true
  analytics: boolean;
  marketing: boolean;
}

const defaultPrefs: CookiePrefs = {
  essential: true,
  analytics: false,
  marketing: false,
};

/** Log consent decision with timestamp (Amendment 13 requirement) */
function logConsent(action: string, prefs: CookiePrefs) {
  const entry = {
    action,
    prefs,
    timestamp: new Date().toISOString(),
    url: window.location.href,
  };
  try {
    const existing = JSON.parse(localStorage.getItem(COOKIE_LOG_KEY) || '[]');
    existing.push(entry);
    // Keep last 50 entries
    if (existing.length > 50) existing.splice(0, existing.length - 50);
    localStorage.setItem(COOKIE_LOG_KEY, JSON.stringify(existing));
  } catch {
    // localStorage may be full
  }
}

/** Remove tracking cookies when rejected */
function removeTrackingCookies() {
  document.cookie.split(';').forEach((c) => {
    const name = c.trim().split('=')[0];
    if (name.startsWith('_ga') || name.startsWith('_fb') || name.startsWith('_gid')) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }
  });
}

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState<CookiePrefs>({ ...defaultPrefs });
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      const timer = setTimeout(() => {
        previousFocusRef.current = document.activeElement as HTMLElement;
        setIsVisible(true);
        setTimeout(() => setIsAnimating(true), 50);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Focus the dialog when it becomes visible
  useEffect(() => {
    if (isAnimating && dialogRef.current) {
      const firstButton = dialogRef.current.querySelector<HTMLButtonElement>('button');
      firstButton?.focus();
    }
  }, [isAnimating]);

  // Handle Escape key
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleRejectAll();
    }
  }, []);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      // Restore focus to previously focused element
      previousFocusRef.current?.focus();
    }, 300);
  };

  /** Accept all cookies */
  const handleAcceptAll = () => {
    const allPrefs: CookiePrefs = { essential: true, analytics: true, marketing: true };
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    localStorage.setItem(COOKIE_PREFS_KEY, JSON.stringify(allPrefs));
    logConsent('accept_all', allPrefs);
    window.dispatchEvent(new Event('cookie-consent-accepted'));
    handleClose();
  };

  /** Save only selected preferences */
  const handleSavePrefs = () => {
    const consentLevel = prefs.analytics || prefs.marketing ? 'accepted' : 'rejected';
    localStorage.setItem(COOKIE_CONSENT_KEY, consentLevel);
    localStorage.setItem(COOKIE_PREFS_KEY, JSON.stringify(prefs));
    logConsent('custom', prefs);

    if (!prefs.analytics && !prefs.marketing) {
      removeTrackingCookies();
    }

    window.dispatchEvent(new Event('cookie-consent-updated'));
    if (prefs.analytics || prefs.marketing) {
      window.dispatchEvent(new Event('cookie-consent-accepted'));
    }
    handleClose();
  };

  /** Reject all non-essential */
  const handleRejectAll = () => {
    const minPrefs: CookiePrefs = { essential: true, analytics: false, marketing: false };
    localStorage.setItem(COOKIE_CONSENT_KEY, 'rejected');
    localStorage.setItem(COOKIE_PREFS_KEY, JSON.stringify(minPrefs));
    logConsent('reject_all', minPrefs);
    removeTrackingCookies();
    handleClose();
  };

  if (!isVisible) return null;

  return (
    <div
      ref={dialogRef}
      className={`fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-[10000] p-0 transition duration-300 ${
        isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
      dir="rtl"
      role="dialog"
      aria-label="ניהול עוגיות ופרטיות"
      aria-describedby="cookie-desc"
      aria-modal="false"
      onKeyDown={handleKeyDown}
    >
      <div className="w-full">
        <div className="bg-gray-900 rounded-xl border border-gray-700/60 shadow-2xl overflow-hidden">
          {/* Main banner */}
          <div className="px-5 py-4">
            <div className="flex items-start gap-3 mb-3">
              <Cookie className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <p id="cookie-desc" className="text-slate-300 text-xs leading-relaxed">
                האתר משתמש בעוגיות חיוניות כדי לעבוד. אם תסכימו, נשתמש גם בעוגיות
                אנליטיקה (Google Analytics) ושיווק (Facebook Pixel) כדי לשפר את השירות.{' '}
                <Link to="/privacy" className="text-tech-cyan-400 hover:text-tech-cyan-300 underline">
                  מדיניות הפרטיות
                </Link>
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 justify-end items-center">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-1 text-slate-400 hover:text-white text-xs transition-colors ml-auto"
                aria-expanded={showDetails}
              >
                <Settings className="h-3.5 w-3.5" aria-hidden="true" />
                ניהול העדפות
                {showDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
              <Button
                onClick={handleRejectAll}
                variant="outline"
                size="sm"
                className="border-slate-600 text-slate-400 hover:bg-slate-800 hover:text-white text-xs px-3 py-1.5 rounded-lg"
              >
                <Shield className="h-3 w-3 ml-1" aria-hidden="true" />
                רק עוגיות חיוניות
              </Button>
              <Button
                onClick={handleAcceptAll}
                size="sm"
                className="bg-green-700 hover:bg-green-800 text-white text-xs px-4 py-1.5 rounded-lg"
              >
                <Check className="h-3 w-3 ml-1" aria-hidden="true" />
                אישור כל העוגיות
              </Button>
            </div>
          </div>

          {/* Expandable granular preferences */}
          {showDetails && (
            <div className="border-t border-gray-700/60 px-5 py-4 bg-gray-950/50 space-y-3">
              {/* Essential — always on */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-medium">עוגיות חיוניות</p>
                  <p className="text-slate-400 text-xs">נדרשות כדי שהאתר יעבוד — רשימת הבקשה, התחברות, הגדרות נגישות</p>
                </div>
                <span className="text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded">תמיד פעילות</span>
              </div>

              {/* Analytics toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-medium">עוגיות אנליטיקה</p>
                  <p className="text-slate-400 text-xs">Google Analytics — ניתוח שימוש באתר</p>
                </div>
                <button
                  onClick={() => setPrefs(p => ({ ...p, analytics: !p.analytics }))}
                  role="switch"
                  aria-checked={prefs.analytics}
                  aria-label="עוגיות אנליטיקה"
                  className={`relative w-10 h-5 rounded-full transition-colors ${
                    prefs.analytics ? 'bg-cyan-500' : 'bg-slate-600'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                      prefs.analytics ? 'translate-x-0.5' : 'translate-x-5'
                    }`}
                  />
                </button>
              </div>

              {/* Marketing toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-medium">עוגיות שיווק</p>
                  <p className="text-slate-400 text-xs">Facebook Pixel — מדידת פרסום</p>
                </div>
                <button
                  onClick={() => setPrefs(p => ({ ...p, marketing: !p.marketing }))}
                  role="switch"
                  aria-checked={prefs.marketing}
                  aria-label="עוגיות שיווק"
                  className={`relative w-10 h-5 rounded-full transition-colors ${
                    prefs.marketing ? 'bg-cyan-500' : 'bg-slate-600'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                      prefs.marketing ? 'translate-x-0.5' : 'translate-x-5'
                    }`}
                  />
                </button>
              </div>

              <Button
                onClick={handleSavePrefs}
                size="sm"
                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white text-xs rounded-lg mt-2"
              >
                שמירת הבחירה
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;

/**
 * Re-open cookie preferences — call from footer or settings.
 * Usage: import { openCookiePreferences } from '@/components/CookieConsent';
 *        onClick={openCookiePreferences}
 */
export function openCookiePreferences() {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  window.location.reload();
}
